import React from "react";

export interface ClientContactData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  location: string;
}

export const emptyClientContact: ClientContactData = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  location: "",
};

const NAME_REGEX = /^[A-Za-z][A-Za-z\s'-]*$/;
const LOCATION_REGEX = /^[A-Za-z][A-Za-z0-9\s,.'-]*$/;

export const getClientContactError = (
  key: keyof ClientContactData,
  value: string,
): string | null => {
  const trimmed = value.trim();
  switch (key) {
    case "firstName":
      if (!trimmed) return "First name is required";
      if (trimmed.length < 2) return "First name must be at least 2 characters";
      if (!NAME_REGEX.test(trimmed)) return "Only letters, spaces, hyphens allowed";
      return null;
    case "lastName":
      if (!trimmed) return "Last name is required";
      if (trimmed.length < 2) return "Last name must be at least 2 characters";
      if (!NAME_REGEX.test(trimmed)) return "Only letters, spaces, hyphens allowed";
      return null;
    case "phoneNumber": {
      const digits = trimmed.replace(/\D/g, "");
      if (!digits) return "Phone number is required";
      if (digits.startsWith("0")) return "Phone number cannot start with 0";
      if (digits.length !== 10) return "Enter a valid 10-digit phone number";
      return null;
    }
    case "location":
      if (!trimmed) return "Location is required";
      if (trimmed.length < 2) return "Location must be at least 2 characters";
      if (!LOCATION_REGEX.test(trimmed)) return "Enter a valid location";
      return null;
    default:
      return null;
  }
};

export const isClientContactComplete = (data: ClientContactData): boolean =>
  (Object.keys(data) as (keyof ClientContactData)[]).every(
    (key) => getClientContactError(key, data[key]) === null,
  );

interface FieldConfig {
  key: keyof ClientContactData;
  label: string;
  placeholder: string;
  half: boolean;
  digitsOnly?: boolean;
  maxLength?: number;
}

const FIELDS: FieldConfig[] = [
  { key: "firstName", label: "First name", placeholder: "eg. John", half: true, maxLength: 50 },
  { key: "lastName", label: "Last name", placeholder: "eg. Doe", half: true, maxLength: 50 },
  {
    key: "phoneNumber",
    label: "Phone number",
    placeholder: "eg . 7869 369 854",
    half: true,
    digitsOnly: true,
    maxLength: 10,
  },
  { key: "location", label: "Location", placeholder: "eg. Mumbai", half: true, maxLength: 100 },
];

interface ClientContactSectionProps {
  data: ClientContactData;
  onChange: (data: ClientContactData) => void;
  touchedFields?: Record<string, boolean>;
  onFieldTouched?: (field: keyof ClientContactData) => void;
}

const ClientContactSection: React.FC<ClientContactSectionProps> = ({
  data,
  onChange,
  touchedFields,
  onFieldTouched,
}) => {
  const handleChange = (key: keyof ClientContactData, value: string) => {
    onChange({ ...data, [key]: value });
  };

  const renderField = (field: FieldConfig, extraClass = "") => {
    const value = data[field.key];
    const touched = touchedFields?.[`contact_${field.key}`];
    const errorMessage = touched ? getClientContactError(field.key, value) : null;
    return (
      <div key={field.key} className={extraClass}>
        <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
          {field.label}
        </label>
        <input
          type={field.digitsOnly ? "tel" : "text"}
          inputMode={field.digitsOnly ? "numeric" : undefined}
          maxLength={field.maxLength}
          value={value}
          onChange={(e) => {
            const raw = e.target.value;
            let next = raw;
            if (field.digitsOnly) {
              next = raw.replace(/\D/g, "").slice(0, field.maxLength ?? 10);
              if (field.key === "phoneNumber") {
                next = next.replace(/^0+/, "");
              }
            } else if (field.maxLength) {
              next = raw.slice(0, field.maxLength);
            }
            handleChange(field.key, next);
          }}
          onBlur={() => onFieldTouched?.(field.key)}
          placeholder={field.placeholder}
          className={`w-full px-4 py-2 border rounded-md text-sm 3xl:text-base font-inter outline-none focus:border-[#0DB5B5]
            ${errorMessage ? "border-red-500" : "border-[#E0E0E0]"}
            placeholder:text-[#9E9E9E]`}
        />
        {errorMessage && (
          <p className="mt-1 text-xs 3xl:text-sm text-red-500">{errorMessage}</p>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-md p-6 space-y-6">
      <div className="grid grid-cols-2 gap-6">
        {FIELDS.slice(0, 2).map((field) => renderField(field))}
      </div>

      <div className="grid grid-cols-4 gap-6">
        {FIELDS.slice(2).map((field) =>
          renderField(field, field.key === "location" ? "col-span-3" : "col-span-1"),
        )}
      </div>
    </div>
  );
};

export default ClientContactSection;
